import { useState, useEffect } from 'react';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { api } from '../lib/api';
import type { Employee } from '../lib/types';

const Icons = {
  org: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4A7CFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  person: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  building: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#8B92A8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" />
      <path d="M8 10h.01" /><path d="M16 10h.01" /><path d="M8 14h.01" /><path d="M16 14h.01" />
    </svg>
  ),
  download: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
};

export default function Reports() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [orgLoading, setOrgLoading] = useState(false);
  const [indLoading, setIndLoading] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultSuccess, setResultSuccess] = useState(true);

  useEffect(() => {
    api.employees.list().then(res => {
      setEmployees(res.employees as unknown as Employee[]);
    }).catch(() => {});
  }, []);

  const handleOrgReport = async () => {
    setOrgLoading(true);
    try {
      await api.reports.organization();
      setResultMessage('組織タレントマップレポートを生成しました。ダウンロードの準備が完了しました。');
      setResultSuccess(true);
      setShowResultModal(true);
    } catch (err) {
      setResultMessage(err instanceof Error ? err.message : 'レポート生成に失敗しました');
      setResultSuccess(false);
      setShowResultModal(true);
    } finally {
      setOrgLoading(false);
    }
  };

  const handleIndReport = async () => {
    if (!selectedEmpId) return;
    setIndLoading(true);
    try {
      await api.reports.individual(selectedEmpId);
      const emp = employees.find(e => e.id === selectedEmpId);
      setResultMessage(`${emp?.name || ''}さんの個人別提案シートを生成しました。`);
      setResultSuccess(true);
      setShowResultModal(true);
    } catch (err) {
      setResultMessage(err instanceof Error ? err.message : 'レポート生成に失敗しました');
      setResultSuccess(false);
      setShowResultModal(true);
    } finally {
      setIndLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-text-main mb-1">レポート出力</h2>
      <p className="text-[13px] text-text-sub mb-6">組織全体・個人別のPDFレポートを出力</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Organization Report */}
        <Card className="text-center !py-8">
          <div className="flex justify-center mb-3">{Icons.org}</div>
          <div className="text-[15px] font-semibold text-text-main mb-1">組織タレントマップ</div>
          <Badge bg="rgba(74,124,255,0.1)" color="#4A7CFF" className="mb-3">PDF</Badge>
          <div className="text-xs text-text-sub mb-5 leading-relaxed">
            全社員のタイプ分布・チーム構成・<br />働き方レコメンドの総合レポート
          </div>
          <Button primary small onClick={handleOrgReport} disabled={orgLoading}>
            <span className="flex items-center gap-1.5 justify-center">
              {orgLoading ? '生成中...' : <>{Icons.download} PDF出力</>}
            </span>
          </Button>
        </Card>

        {/* Individual Report */}
        <Card className="text-center !py-8">
          <div className="flex justify-center mb-3">{Icons.person}</div>
          <div className="text-[15px] font-semibold text-text-main mb-1">個人別提案シート</div>
          <Badge bg="rgba(74,222,128,0.1)" color="#4ADE80" className="mb-3">PDF</Badge>
          <div className="text-xs text-text-sub mb-4 leading-relaxed">
            社員ごとの働き方レコメンド・<br />強み・注意点をまとめた個別シート
          </div>
          <div className="mb-3">
            <select
              value={selectedEmpId}
              onChange={e => setSelectedEmpId(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-[13px] outline-none focus:border-primary w-full max-w-[220px]"
            >
              <option value="">社員を選択</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.name}（{e.personality_type || '未入力'}）</option>
              ))}
            </select>
          </div>
          <Button primary small onClick={handleIndReport} disabled={indLoading || !selectedEmpId}>
            <span className="flex items-center gap-1.5 justify-center">
              {indLoading ? '生成中...' : <>{Icons.download} PDF出力</>}
            </span>
          </Button>
        </Card>

        {/* Phase 2 */}
        <Card className="text-center !py-8 border-dashed opacity-50">
          <div className="flex justify-center mb-3">{Icons.building}</div>
          <div className="text-[15px] font-semibold text-text-main mb-1">組織図レポート</div>
          <Badge bg="rgba(139,146,168,0.1)" color="#8B92A8" className="mb-3">Phase 2</Badge>
          <div className="text-xs text-text-sub mb-5 leading-relaxed">
            現在 vs 推奨の組織図<br />比較レポート
          </div>
          <Button small disabled>Coming Soon</Button>
        </Card>
      </div>

      {/* Result Modal */}
      <Modal open={showResultModal} onClose={() => setShowResultModal(false)} title="レポート生成">
        <div className="text-center py-2">
          <div className={`text-3xl mb-3 ${resultSuccess ? 'text-success' : 'text-accent'}`}>
            {resultSuccess ? (
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            ) : (
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto">
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            )}
          </div>
          <p className="text-[13px] text-text-sub mb-4">{resultMessage}</p>
          <Button primary small onClick={() => setShowResultModal(false)}>閉じる</Button>
        </div>
      </Modal>
    </div>
  );
}
